import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    Typography,
    TableBody,
    Checkbox,
    Button,
} from "@mui/material";
import { t } from "i18next";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";

import BasicDatePicker from "components/DatePicker/DatePicker";
import { ReadMoreText } from "legacyNext/components/ReadMoreText/ReadMoreText";
import { ReductionProposal } from "legacyNext/types";
import { currencyFormatter } from "legacyNext/utils";
import { formatNumberLocale, gramsToKg } from "utils/report.helpers";

import { S } from "./reduction-plan.styles";

type Props = {
    proposals: Array<ReductionProposal>;
    onChange: (
        id: string,
        key: "startYear" | "enabled",
        value: null | number | boolean
    ) => void;
};

export const ReductionPlanTable = (props: Props) => {
    const { proposals, onChange } = props;

    const [rowsDisplayed, setRowsDisplay] = useState(5);

    const labels = [
        t("legacyNext.initiative", "initiative"),
        t("legacyNext.annualEnergySavings", "annual energy savings"),
        t("legacyNext.annualC02eReduction", "annual CO2 reduction"),
        t("legacyNext.investment", "investment"),
        t("legacyNext.yearOfImplementation", "year of implementation"),
        t("legacyNext.selectMeasure", "select measure"),
    ];

    const summaryRows = useMemo(() => {
        const displayedProposals = proposals.slice(0, rowsDisplayed);

        return displayedProposals.map((summary, index) => (
            <TableRow key={`${summary.category}-${index}`}>
                <TableCell>
                    <Typography fontWeight="bold">
                        ({summary.id}) - {summary.title}
                    </Typography>
                    <ReadMoreText text={summary.description} maxLength={50} />
                </TableCell>
                <TableCell>
                    <Typography variant="body2">
                        {currencyFormatter(summary.costSaving)}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">
                        {formatNumberLocale(
                            gramsToKg(summary.emissionSaving),
                            t,
                            { max: 0 }
                        )}
                        kg CO2e
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">
                        {currencyFormatter(summary.investment)}
                    </Typography>
                </TableCell>
                <TableCell>
                    <BasicDatePicker
                        views={["year"]}
                        value={
                            summary.startYear
                                ? DateTime.fromObject({
                                      year: summary.startYear,
                                  })
                                : null
                        }
                        minDate={DateTime.now()}
                        maxDate={DateTime.fromISO("2050")}
                        onChange={(date) =>
                            onChange(
                                summary.id,
                                "startYear",
                                date?.year || null
                            )
                        }
                    />
                </TableCell>
                <TableCell>
                    <Checkbox
                        color="primary"
                        checked={summary.enabled}
                        onClick={() =>
                            onChange(summary.id, "enabled", !summary.enabled)
                        }
                    />
                </TableCell>
            </TableRow>
        ));
    }, [proposals, rowsDisplayed, onChange]);

    const onDisplayMore = useCallback(() => {
        setRowsDisplay((prev) => prev + 5);
    }, []);

    return (
        <>
            <S.View>
                <Table>
                    <TableHead>
                        <S.HeaderRow>
                            {labels.map((item, index) => (
                                <TableCell key={index}>
                                    <Typography
                                        variant="h5"
                                        fontWeight="bold"
                                        color="white"
                                    >
                                        {item}
                                    </Typography>
                                </TableCell>
                            ))}
                        </S.HeaderRow>
                    </TableHead>
                    <TableBody>{summaryRows}</TableBody>
                </Table>
            </S.View>
            <S.DisplayMore>
                <Typography variant="body2" color="text.secondary">
                    {t(
                        "legacyNext.displayingRows",
                        "showing {{displayed}} of {{total}}",
                        {
                            displayed: Math.min(
                                rowsDisplayed,
                                proposals.length
                            ),
                            total: proposals.length,
                        }
                    )}
                </Typography>
                {rowsDisplayed < proposals.length && (
                    <Button variant="text" onClick={onDisplayMore} size="small">
                        {t("legacyNext.displayMore", "display more")}
                    </Button>
                )}
            </S.DisplayMore>
        </>
    );
};
